import React, { useMemo, useState } from 'react';
import XCircleIcon from '../icons/XCircleIcon';
import type { StaffMember } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface ManagerApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: (staffId: number, reason: string) => void;
  discountAmount: number;
  discountPercentage: number;
  orderTotal: number;
  staffMembers: StaffMember[];
  currentUser?: StaffMember;
}

const ManagerApprovalModal: React.FC<ManagerApprovalModalProps> = ({
  isOpen,
  onClose,
  onApprove,
  discountAmount,
  discountPercentage,
  orderTotal,
  staffMembers,
  currentUser
}) => {
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(
    currentUser?.id || null
  );
  const [approvalReason, setApprovalReason] = useState('');
  const [pin, setPin] = useState('');

  if (!isOpen) return null;

  // Filter staff to show only managers (you may need to adjust this based on your role system)
  const managers = useMemo(() => staffMembers.filter(
    staff => staff.role.toLowerCase().includes('manager') || 
             staff.role.toLowerCase().includes('admin') ||
             staff.id === currentUser?.id
  ), [staffMembers, currentUser]);

  const selectedManager = managers.find(m => m.id === selectedManagerId);

  const pinValidator = (value: string) => /^\d{0,6}$/.test(value);
  const pinField = useSoftKeyboardField({
    id: 'manager-pin',
    type: 'numeric',
    label: selectedManager ? `PIN for ${selectedManager.name}` : 'Manager PIN',
    value: pin,
    onChange: (value) => {
      if (pinValidator(value)) setPin(value);
    },
    manualValidator: pinValidator,
    maskValue: true,
  });
  const reasonField = useSoftKeyboardField({
    id: 'manager-approval-reason',
    type: 'alphanumeric',
    label: 'Approval Reason',
    value: approvalReason,
    onChange: setApprovalReason,
  });

  const handleApprove = () => {
    if (!selectedManagerId) {
      alert('Please select a manager');
      return;
    }
    if (!approvalReason.trim()) {
      alert('Please provide a reason for approval');
      return;
    }
    if (selectedManager && selectedManager.pin !== pin) {
      alert('Invalid PIN. Please try again.');
      setPin('');
      return;
    }
    onApprove(selectedManagerId, approvalReason.trim());
    handleClose();
  };

  const handleClose = () => {
    setSelectedManagerId(currentUser?.id || null);
    setApprovalReason('');
    setPin('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Manager Approval Required</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Discount Summary */}
          <div className="bg-yellow-50 border border-yellow-400 rounded-md p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">Discount Details</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-yellow-700">Order Total:</span>
                <span className="font-semibold">${orderTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-yellow-700">Discount Amount:</span>
                <span className="font-semibold text-red-600">-${discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-yellow-700">Discount Percentage:</span>
                <span className="font-semibold text-red-600">{discountPercentage.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-yellow-300">
                <span className="text-yellow-800 font-semibold">New Total:</span>
                <span className="font-bold text-yellow-800">${(orderTotal - discountAmount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Manager Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Approving Manager *
            </label>
            <select
              value={selectedManagerId || ''}
              onChange={(e) => setSelectedManagerId(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="">Select a manager...</option>
              {managers.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.name} ({manager.role})
                </option>
              ))}
            </select>
          </div>

          {/* PIN Entry */}
          {selectedManager && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PIN for {selectedManager.name} *
              </label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                {...pinField}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Enter PIN"
              />
            </div>
          )}

          {/* Approval Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Approval Reason *
            </label>
            <textarea
              value={approvalReason}
              {...reasonField}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              rows={3}
              placeholder="e.g., Customer complaint resolved, VIP customer, Special promotion..."
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleClose}
              className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={!selectedManagerId || !approvalReason.trim() || !pin}
              className="flex-1 py-2 px-4 bg-green-500 text-white rounded-md font-semibold hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              Approve Discount
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerApprovalModal;

